import { Permissions, usePermissions } from '@app/src/auth/permissions'
import LinkButton from '@app/src/components/LinkButton'
import { useAccount } from '@app/src/context/AccountContext'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import PublicContactEnforcer from '@app/src/context/PublicContactEnforcer'
import useFeatureFlagsByAccessor from '@app/src/hooks/useFeatureFlagsByAccessor'
import { Solutions } from '@app/src/wf-constants'
import CloseIcon from '@mui/icons-material/Close'
import MenuIcon from '@mui/icons-material/Menu'
import { AppBar, Box, Drawer, Grid, IconButton, Toolbar, Tooltip } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import AiJobsStatusIndicator from '../HorizontalMenu/AiJobsStatusIndicator'
import AccountMenu from './LegacyAccountMenu'
import AiFileRepositoryLink from './LegacyAiFileRepositoryLink'
import ConfigurationsMenuItem from './LegacyConfigurationsMenuItem'
import NavbarItems from './LegacyNavbarItems'
import NotificationsMenuItem from './LegacyNotificationsMenuItem'
import OrganizationMenu from './LegacyOrganizationMenu'
import PlatformReleasesMenuItem from './LegacyPlatformReleasesMenuItem'
import SustainabilityLibraryMenuItem from './LegacySustainabilityLibraryMenuItem'
import YourDataMenuItem from './LegacyYourDataMenuItem'
import WorldfavorLogo from './LegacyWorldfavorLogo'

const APP_NAVBAR_HEIGHT = 56

const Navbar = (): JSX.Element => {
  const { formatMessage } = useIntl()
  const { account } = useAccount()
  const { scope } = useAuthentication()
  const { hasPermission } = usePermissions()
  const { solution = '' } = scope
  const [isResponsiveMenuOpen, setResponseMenuOpen] = useState(false)
  const { orgOrAccessorHasFeature: suggestedAnswers } = useFeatureFlagsByAccessor({
    featureToggleName: 'suggested-answers',
    enabled: hasPermission(Permissions.TRANSPARENCY_USER),
    priorityValue: false,
  })

  const displayableSolutions = Object.values(Solutions)

  const solutionExists = solution && displayableSolutions.includes(String(solution))

  return (
    <AppBar
      sx={({ palette }) => ({
        boxShadow: 'none',
        backgroundColor: palette.background.default,
        borderBottom: `1px solid ${palette.grey[100]}`,
        borderRadius: 0,
        '& .MuiToolbar-root': {
          minHeight: APP_NAVBAR_HEIGHT,
        },
      })}
      position="sticky"
      color="transparent"
      data-testid="navbar"
    >
      <PublicContactEnforcer />
      <Toolbar
        disableGutters
        sx={{
          minHeight: APP_NAVBAR_HEIGHT,
          justifyContent: 'space-between',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            height: APP_NAVBAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {(scope.solution !== 'default' || scope.role?.toLowerCase() === 'worldfavoremployeeadmin') && (
            <>
              <Tooltip arrow title={formatMessage({ id: 'navbar.menu' })}>
                <IconButton
                  color="default"
                  aria-label="menu"
                  onClick={(): void => setResponseMenuOpen(true)}
                  size="medium"
                  sx={{ px: 2, borderRadius: 0, height: '100%', display: { xs: 'block', md: 'none' } }}
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Drawer
                open={isResponsiveMenuOpen}
                anchor="left"
                sx={{
                  width: '320px',
                  height: '100%',
                  margin: 0,
                  borderRadius: 0,
                }}
              >
                <Grid container>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <IconButton onClick={(): void => setResponseMenuOpen(false)} size="medium">
                        <CloseIcon fontSize="medium" />
                      </IconButton>
                    </Box>
                  </Grid>
                  <NavbarItems flexDirection="column" closeMenuDrawer={() => setResponseMenuOpen(false)} />
                </Grid>
              </Drawer>
            </>
          )}

          {scope.solution !== 'default' || scope.role?.toLowerCase() === 'worldfavoremployeeadmin' ? (
            <OrganizationMenu account={account}></OrganizationMenu>
          ) : (
            <Box sx={{ ml: 2, mr: 1.5 }}>
              <LinkButton
                startIcon={<WorldfavorLogo />}
                to="/"
                sx={{
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
                disableRipple
              ></LinkButton>
            </Box>
          )}

          <Box height="100%" sx={{ display: { xs: 'none', md: 'flex' } }}>
            <NavbarItems />
          </Box>
        </Box>

        <Box
          sx={{
            height: APP_NAVBAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {account && (
            <>
              <PlatformReleasesMenuItem />
              <AiJobsStatusIndicator />
              {solutionExists && solution !== Solutions.Transparency && <SustainabilityLibraryMenuItem />}
              {solutionExists && solution === Solutions.Transparency && <YourDataMenuItem />}
              {suggestedAnswers && hasPermission(Permissions.TRANSPARENCY_USER) && <AiFileRepositoryLink />}
              {hasPermission(Permissions.ACCESS_SOLUTION_CONFIGURATION) && <ConfigurationsMenuItem />}
              {solutionExists && <NotificationsMenuItem />}
              <AccountMenu account={account} />
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default Navbar
